import React, { useEffect } from 'react';
import setSeo from '../../utils/seo.js';
import BackToTop from '../../components/BackToTop.jsx';
import case2 from '../../assets/case2.webp';

/**
 * Individual case study: Entertainment Industry – Custom Artist Management CRM
 * Full case study page with detailed implementation and results
 */
const EntertainmentCRM = () => {
  useEffect(() => {
    setSeo({
      title: 'Entertainment Artist Management CRM Case Study - 30% More Engagement | Aakeeo',
      description:
        'Discover how our custom artist management CRM solution increased audience engagement by 30% and reduced coordination efforts by 50% for an entertainment company managing multiple artists and events.',
      keywords: ['entertainment CRM', 'artist management software', 'event management CRM', 'custom CRM solution', 'entertainment case study', 'artist booking system'],
      canonical: window.location.href,
      og: {
        type: 'article',
        image: 'https://aakeeo.com' + case2
      },
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'Article',
        'headline': 'Entertainment Industry – Custom Artist Management CRM',
        'description': 'A tailored CRM solution for managing artists, shows, and concerts that increased audience engagement by 30% and reduced coordination efforts by 50%.',
        'image': 'https://aakeeo.com' + case2,
        'author': {
          '@type': 'Organization',
          'name': 'Aakeeo Technologies'
        },
        'publisher': {
          '@type': 'Organization',
          'name': 'Aakeeo Technologies',
          'logo': {
            '@type': 'ImageObject',
            'url': 'https://aakeeo.com/Aakeeo_favicon.png'
          }
        }
      }
    });
  }, []);
  return (
    <div className="case-study-page">
      <section className="page-hero case-studies-hero">
        <video className="page-hero-video" autoPlay muted loop>
          <source src="/background.mp4" type="video/mp4" />
        </video>
        <div className="page-hero-content">
          <h1 className="page-hero-title">Entertainment Industry – Custom Artist Management CRM</h1>
          <p className="page-hero-subtitle">A tailored CRM solution for managing artists, shows, and concerts that increased audience engagement by 30% and reduced coordination efforts by 50%.</p>
        </div>
      </section>

      <article className="section reveal">
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 2rem' }}>
          <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
            <img
              src={case2}
              alt="Entertainment CRM Success Story"
              style={{
                width: '100%',
                maxWidth: '600px',
                height: '300px',
                objectFit: 'cover',
                borderRadius: '15px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
              }}
            />
          </div>

          <div style={{ fontSize: '1.1rem', lineHeight: '1.8', color: 'var(--text-light)' }}>
            <h2 style={{ color: 'var(--secondary-color)', marginBottom: '1rem', fontSize: '1.8rem' }}>
              Challenge
            </h2>

            <p style={{ marginBottom: '1.5rem' }}>
              An entertainment company managing multiple artists, shows, and concerts required a tailored CRM solution. Standard CRMs were unsuitable because they primarily revolved around leads, contacts, and opportunities. The client's workflows involved:
            </p>

            <ul style={{ marginBottom: '1.5rem', paddingLeft: '2rem', listStyle: 'disc' }}>
              <li style={{ marginBottom: '0.5rem' }}>Managing artists and their contracts</li>
              <li style={{ marginBottom: '0.5rem' }}>Scheduling shows and concerts</li>
              <li style={{ marginBottom: '0.5rem' }}>Handling ticketing, invoices, and marketing campaigns</li>
              <li style={{ marginBottom: '0.5rem' }}>Coordinating with team members over email, calendars, and WhatsApp</li>
            </ul>

            <h2 style={{ color: 'var(--secondary-color)', marginBottom: '1rem', fontSize: '1.8rem' }}>
              Requirements
            </h2>

            <ul style={{ marginBottom: '1.5rem', paddingLeft: '2rem', listStyle: 'disc' }}>
              <li style={{ marginBottom: '0.5rem' }}>Custom CRM structure beyond default SuiteCRM modules</li>
              <li style={{ marginBottom: '0.5rem' }}>Unified dashboard for artists, shows, and invoices</li>
              <li style={{ marginBottom: '0.5rem' }}>Marketing automation for event promotion</li>
              <li style={{ marginBottom: '0.5rem' }}>Calendar synchronization for artist schedules</li>
              <li style={{ marginBottom: '0.5rem' }}>Messaging integration with Gmail and WhatsApp</li>
            </ul>

            <h2 style={{ color: 'var(--secondary-color)', marginBottom: '1rem', fontSize: '1.8rem' }}>
              Solution Architecture
            </h2>

            <p style={{ marginBottom: '1.5rem' }}>
              We deployed a custom SuiteCRM instance with deep module customizations:
            </p>

            <h3 style={{ color: 'var(--secondary-color)', marginBottom: '1rem', fontSize: '1.4rem' }}>
              Custom Modules
            </h3>
            <p style={{ marginBottom: '1.5rem' }}>
              Artists, Shows, Concerts, Invoices, Tickets
            </p>

            <h3 style={{ color: 'var(--secondary-color)', marginBottom: '1rem', fontSize: '1.4rem' }}>
              Workflow Automation
            </h3>
            <p style={{ marginBottom: '1.5rem' }}>
              Automated event reminders and follow-ups
            </p>

            <h3 style={{ color: 'var(--secondary-color)', marginBottom: '1rem', fontSize: '1.4rem' }}>
              Integrations
            </h3>
            <ul style={{ marginBottom: '1.5rem', paddingLeft: '2rem', listStyle: 'disc' }}>
              <li style={{ marginBottom: '0.5rem' }}>Google Calendar API to schedule artist shows and rehearsals</li>
              <li style={{ marginBottom: '0.5rem' }}>Gmail API for centralized communication logs</li>
              <li style={{ marginBottom: '0.5rem' }}>WhatsApp Business API for promotional campaigns and instant updates</li>
            </ul>

            <h2 style={{ color: 'var(--secondary-color)', marginBottom: '1rem', fontSize: '1.8rem' }}>
              Technologies Used
            </h2>

            <ul style={{ marginBottom: '1.5rem', paddingLeft: '2rem', listStyle: 'disc' }}>
              <li style={{ marginBottom: '0.5rem' }}>SuiteCRM 7.14.x (custom module development)</li>
              <li style={{ marginBottom: '0.5rem' }}>REST APIs for Google Calendar and Gmail integration</li>
              <li style={{ marginBottom: '0.5rem' }}>Twilio WhatsApp Business API for communication</li>
              <li style={{ marginBottom: '0.5rem' }}>PHP + MySQL for CRM customizations</li>
              <li style={{ marginBottom: '0.5rem' }}>n8n for workflow orchestration and automation</li>
            </ul>

            <h2 style={{ color: 'var(--secondary-color)', marginBottom: '1rem', fontSize: '1.8rem' }}>
              Impact
            </h2>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1rem',
              marginBottom: '2rem'
            }}>
              <div style={{
                background: 'rgba(230, 57, 70, 0.1)',
                padding: '1.5rem',
                borderRadius: '10px',
                textAlign: 'center'
              }}>
                <h3 style={{ color: 'var(--primary-color)', fontSize: '2rem', marginBottom: '0.5rem' }}>30%</h3>
                <p style={{ margin: 0 }}>Higher Audience Engagement</p>
              </div>
              <div style={{
                background: 'rgba(0, 119, 182, 0.1)',
                padding: '1.5rem',
                borderRadius: '10px',
                textAlign: 'center'
              }}>
                <h3 style={{ color: 'var(--accent-color)', fontSize: '2rem', marginBottom: '0.5rem' }}>50%</h3>
                <p style={{ margin: 0 }}>Reduction in Manual Coordination</p>
              </div>
            </div>

            <ul style={{ marginBottom: '1.5rem', paddingLeft: '2rem', listStyle: 'disc' }}>
              <li style={{ marginBottom: '0.5rem' }}>Unified artist and event management platform</li>
              <li style={{ marginBottom: '0.5rem' }}>Scalable CRM that could accommodate future fields like sponsorships and merchandising</li>
            </ul>

            <div style={{
              background: 'rgba(230, 57, 70, 0.1)',
              padding: '1.5rem',
              borderRadius: '10px',
              borderLeft: '4px solid var(--primary-color)',
              marginTop: '2rem'
            }}>
              <p style={{ margin: 0, fontStyle: 'italic' }}>
                Ready to streamline your entertainment business? Contact Aakeeo today to discover how our custom CRM solutions can transform your artist management and event coordination processes.
              </p>
            </div>
          </div>
        </div>
      </article>
        <BackToTop />
    </div>
  );
};

export default EntertainmentCRM;